/**
 * Junction Registry
 *
 * The chip element each junction subtype displays: link → record, tagging → tag,
 * attachment → asset. Grid group headers resolve it via `getJunctionElement`;
 * junction editing lives in the reference inputs (form), persistence in
 * `@auger/core` (records.update) and id resolution in `resolveTargets`.
 *
 * Keyed by `JunctionType`, `Partial` so a subtype without an element is a
 * compile-time-legal omission. Resolution walks the FieldType chain leaf → root,
 * so a leaf (image/document/video) inherits `attachment`'s element.
 *
 * Open, like the field component registry: this package registers the elements
 * it owns, an app registers the rest.
 */
import type { Component } from "vue"
import {
  type Field,
  FIELD_TYPES,
  type FieldType,
  getFieldType,
  type JunctionType,
} from "@auger/core/fields"

export interface JunctionRegistryEntry {
  element: Component
}

export const JUNCTION_REGISTRY: Partial<Record<JunctionType, JunctionRegistryEntry>> = {}

/** Add junction elements; a later registration for a subtype replaces the earlier one. */
export function registerJunctionElements(
  elements: Partial<Record<JunctionType, JunctionRegistryEntry>>
): void {
  Object.assign(JUNCTION_REGISTRY, elements)
}

/** The JUNCTION_REGISTRY entry for a junction subtype, resolved by walking the
 *  type chain leaf → root (so leaves inherit their parent's entry). Null for
 *  non-junction fields. */
export function getJunctionEntry(field: Pick<Field, "type">): JunctionRegistryEntry | null {
  let current = getFieldType(field.type) as FieldType | undefined
  while (current) {
    const entry = JUNCTION_REGISTRY[current.name as JunctionType]
    if (entry) return entry
    current = FIELD_TYPES[current.parent ?? ""]
  }
  return null
}

/** The chip element a junction subtype displays (RecordElement, TagElement, …).
 *  Null for non-junction fields. */
export function getJunctionElement(field: Pick<Field, "type">): Component | null {
  return getJunctionEntry(field)?.element ?? null
}
